import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FormBuilder, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  NgbDropdownModule,
  NgbModalModule,
  NgbModal,
  NgbTooltipModule,
  NgbAccordionModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Column } from '../column.model';
import { TableDetails } from '../table-details.model';
import {
  CustomFormatCurrencyPipe,
  CustomFormatDatePipe,
  ToasterService,
} from '@anddone/ui-utilities';
import { AppSetting } from '../../../../../../constants/appsetting.constant';
import { TableFilterService } from '../table-filter.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { AllyCommonService } from '../../../../../../services/ally-common-service';
import { Loader } from '../../../../../layout/loader/loader';
import { RollApi } from '../../../users/roll-api/roll-api';
import { ValidationConstant } from '../../../../../../constants/validation.constant';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  imports: [
    NgClass,
    CustomFormatDatePipe,
    Loader,
    FormsModule,
    TranslateModule,
    NgbTooltipModule,
    NgbDropdownModule,
    NgbModalModule,
    CustomFormatCurrencyPipe,
    NgbAccordionModule,
    NgTemplateOutlet,
  ],
})
export class TableComponent implements OnInit {
  // @Input() InputData: any;
  // @Output() OutputData = new EventEmitter();
  @ViewChild('userTable') userTable!: ElementRef;
  @Output() nestedRowClicked = new EventEmitter<void>();
  @Output() exportOptionClicked = new EventEmitter<string>();

  defaultPageSetting: { itemsPerPage: number | null } = {
    itemsPerPage: null,
  };
  defaultItemPerPage = ValidationConstant.defaultPageSize;
  paginationForm: any;
  pager: any = {};
  page = 1;
  totalRowCount = 0;
  itemPerPage!: any[];
  exportFileOptions = AppSetting.exportFileOptions;
  isLoader = signal<boolean>(false);
  columns: Column[] = [];
  tableDetails!: TableDetails;
  apiModalRef: any;
  clipboard: Clipboard = new Clipboard();
  tableId = '';
  @Input() tabs?: { id: string; label: string; tableDetails: TableDetails; columns: Column[] }[];
  @Input() activeTabId?: string;
  @Output() tabChange = new EventEmitter<string>();

  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  public allyCommonService = inject(AllyCommonService);
  private tableFilterService = inject(TableFilterService);
  public modalService = inject(NgbModal);

  ngOnInit(): void {
    // Subscribe to the loader to toggle loader animation state, especially during API calls.
    this.allyCommonService.getIsLoader()?.subscribe((loaderState) => {
      this.isLoader.set(loaderState);
    });

    // Subscribe to the column details configuration set at the page level.
    this.tableFilterService.tableFilterColumns?.subscribe((columns: Column[]) => {
      this.columns = columns;
    });

    // Subscribe to tableDetails observable for table config and table data as it is populated.
    this.tableFilterService.tableDetails?.subscribe((tableDetails: TableDetails) => {
      this.tableDetails = tableDetails;
    });

    this.tableId = this.tableDetails?.tableId;

    // Subscribe to the pager to handle table pagination.
    this.tableFilterService.searchInput.subscribe((searchInput) => {
      this.pager = searchInput.pager;
    });

    this.setPageSetting();
    if (this.pager.resultPerPage != this.defaultItemPerPage) {
      this.defaultItemPerPage = this.pager.resultPerPage;
    }
    /* form declaration */
    this.paginationForm = this.formBuilder.group({
      resultsPerPage: [this.defaultItemPerPage, []],
    });

    // if( this.defaultItemPerPage != this.tableDetails.defaultItemPerPage )
    //   this.defaultItemPerPage = this.tableDetails.defaultItemPerPage;

    // Reset the itemsPerPage option to a fresh deep copy of the appsetting
    this.itemPerPage = JSON.parse(JSON.stringify(AppSetting.paginationItemPerPage));

    // Make sure the default that was configured is in the list.
    if (!this.itemPerPage.some((item) => item.value === this.tableDetails?.defaultItemPerPage)) {
      this.itemPerPage.push({ value: this.tableDetails?.defaultItemPerPage });
      this.itemPerPage.sort((a, b) => a.value - b.value);
    }
  }

  // Trigger for when the user clicks a sortable row. Starts with default and toggles between asc and desc.
  sortTypeToggle(column: Column) {
    if (!column.isSortable) {
      return;
    }
    const sort = column.sortState == 'none' || column.sortState == 'desc' ? 'asc' : 'desc';
    this.resetSort();
    column.sortState = sort;
    this.tableFilterService.updateSort(column);
  }

  // Check to see if a row is marked as revealed for a concealed column

  toggleConcealContent(column: Column, rowID: string | number): void {
    if (!column.revealedContent) {
      column.revealedContent = [];
    }
    const index = column.revealedContent.indexOf(rowID);
    if (index > -1) {
      // id exists, remove it
      column.revealedContent.splice(index, 1);
    } else {
      // id doesn't exist, add it
      column.revealedContent.push(rowID);
    }
  }

  isRevealed(column: Column, rowID: string | number): boolean {
    if (!column.revealedContent) {
      return false;
    }
    return column.revealedContent.indexOf(rowID) > -1;
  }

  // Sets all columns to non-sorted.
  resetSort() {
    for (const column of this.columns) {
      column.sortState = 'none';
    }
  }

  // Get the class we will assign to the status display based on the statusColors object defined on the column.
  getStatusClass(statusValue: any, column: Column): string | null {
    if (!statusValue || !column.statusColors) {
      return null;
    }

    const lowerStatusValue = statusValue.toString().toLowerCase();

    // Find the key in statusColors that matches case-insensitively
    const matchedKey = Object.keys(column.statusColors).find(
      (key) => key.toLowerCase() === lowerStatusValue
    );

    if (!matchedKey) {
      return null;
    }

    return column.statusColors[matchedKey];
  }

  getMethodClass(methodName: any): string | null {
    methodName = methodName?.toUpperCase();
    switch (methodName) {
      case 'VISA':
        return 'visa';
      case 'MASTERCARD':
        return 'mastercard';
      case 'ACH US DIRECT DEBIT':
        return 'ach';
      case 'DINERS CLUB':
        return 'diners-club';
      case 'DISCOVER':
        return 'discover';
      case 'AMEX':
        return 'amex';
    }
    return null;
  }

  // Handle the operation row.
  onActionMenuClick(operation: string | {}, row: any) {
    const operationObject: any = {};
    operationObject.operation = operation;
    operationObject.row = row;

    // Send to the service to emit the action which is picked up at the page level.
    this.tableFilterService.triggerAction(operationObject);
  }

  // Update the page selected by the user.
  updatePage(page: any) {
    this.tableFilterService.updatePage(page);
  }

  /* function to apply per page item limit class to table  */
  setTableLimit() {
    const tableEle = this.userTable.nativeElement;
    if (this.pager.resultPerPage == '10') {
      tableEle.classList.remove('limit-25');
      tableEle.classList.remove('limit-50');
      tableEle.classList.add('limit-10');
    } else if (this.pager.resultPerPage == '25') {
      tableEle.classList.remove('limit-10');
      tableEle.classList.remove('limit-50');
      tableEle.classList.add('limit-25');
    } else if (this.pager.resultPerPage == '50') {
      tableEle.classList.remove('limit-10');
      tableEle.classList.remove('limit-25');
      tableEle.classList.add('limit-50');
    }
  }

  /* Handle the row click action */
  selectRow(row: any) {
    // If the rowClickURI is set, we navigate to that route.
    if (this.tableDetails.rowClickURI)
      if (
        this.tableDetails.rowClickExtraParams &&
        this.tableDetails.rowClickExtraParams.length > 0
      ) {
        // to handle transaction table row click
        const params: Record<string, any> = {};
        for (const obj of this.tableDetails.rowClickExtraParams) {
          params[obj.param] = row[obj.key];
        }
        this.router.navigate([this.tableDetails.rowClickURI, row.id, params]);
      } else {
        this.router.navigate([this.tableDetails.rowClickURI, row.id]);
      }

    // If alternate behavior is needed beyond routing, it can be configured as a callback.
    if (this.tableDetails.rowClickCallback) {
      this.tableFilterService.triggerRowClickCallback();
    }
  }

  /* function to store page setting in cookie */
  setPageSetting() {
    this.defaultPageSetting.itemsPerPage = this.defaultItemPerPage;
  }

  showApiModal() {
    this.apiModalRef = this.modalService.open(RollApi, { scrollable: true });
  }

  /* item per page dropdown change event */
  onPageCountChange(value: any) {
    this.defaultItemPerPage = Number(value);
    this.paginationForm.controls.resultsPerPage.patchValue(value);
    this.pager = this.tableFilterService.initiatePager(value);
    this.tableFilterService.updateItemsPerPage(this.defaultItemPerPage);
    this.setPageSetting();
  }

  open(content: any) {
    this.modalService.open(content);
  }

  close(content: any) {
    this.modalService.dismissAll(content);
  }

  handleNestedRowClick(row: any, event: Event) {
    event.stopPropagation();
    this.nestedRowClicked.emit(row);
  }

  handleExportOptionClick(option: string, modalContent: unknown): void {
    this.exportOptionClicked.emit(option);
    if (
      (this.tableDetails?.countNumber?.() ?? 0) > 0 &&
      (this.tableDetails?.tableData?.length ?? 0) > 0
    ) {
      this.open(modalContent);
    }
  }

  handleTabClick(tabId: string) {
    this.activeTabId = tabId;
    this.tabChange.emit(tabId);
    this.tableId = tabId;
  }
}
